import { useEffect, useId, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Modal, Button } from "react-bootstrap";
import { Document, Page } from "react-pdf";
import { useDebounce } from "use-debounce";

import { getClientId, getCaseId, formatCalendarDate, calculateAge } from "../../Utils/helper";
import { PDF_LOAD_OPTIONS } from "../../Utils/pdfLoadOptions";
import { usePdfPageWindow } from "../../Utils/usePdfPageWindow";
import { api_without_cancellation } from "../../api/api";
import PdfNavigation from "../DocumentModal/PdfNavigation";
import { PdfThumbnailRail, PdfThumbnailPages } from "../DocumentModal/PdfThumbnail";
import avatarIcon from "../../assets/images/avatar.svg";
import birthdayIcon from "../../assets/images/birthdayicon.svg";
import incidentIcon from "../../assets/images/incident.svg";
import printIcon from "../../assets/print.svg";
import downloadIcon from "../../assets/download.svg";
import "../../Utils/pdfWorker";
import "../DocumentModal/Documents.css";
import "../DocumentModal/DocumentModal.css";
import "./DocumentLinkViewer.css";

const pdfPageStyle = {
  position: "relative",
  margin: "5px auto",
  boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.1), 0 6px 20px 0 rgba(0, 0, 0, 0.09)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
};

// A separate, controlled viewer for document links. Callers own the link target
// and reopen their document selector through onNewDocLink.
const DocumentLinkViewer = ({
  show: isVisible,
  handleClose: hideDocumentModal,
  documentURL,
  documentData = {},
  docLinkTo,
  onNewDocLink,
  initialSearchTerm = "",
}) => {
  const documentSearchId = useId();
  const titleId = useId();
  const [numPages, setNumPages] = useState(null);
  const [error, setError] = useState("");
  const [searchError, setSearchError] = useState("");
  const [page, setPage] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });
  // Real size of every page that has rendered at least once, so placeholders
  // for pages not yet rendered are the right height and nothing shifts.
  const [pageSizes, setPageSizes] = useState({});
  // Whether some page has already set the baseline size and fit zoom.
  const baselineSized = useRef(false);
  const [containerRef, setContainerRef] = useState(null);
  const [railScrollEl, setRailScrollEl] = useState(null);
  const [railPortalEl, setRailPortalEl] = useState(null);
  const [searchString, setSearchString] = useState("");
  // Wait until the user pauses before starting PDF extraction/API work. A
  // short 150ms delay fired repeatedly during normal typing on large files.
  const [debouncedSearchString] = useDebounce(searchString, 500);
  const [ocrGeometryByPage, setOcrGeometryByPage] = useState({});
  const [ocrSearchPages, setOcrSearchPages] = useState([]);
  const [ocrSearchPagination, setOcrSearchPagination] = useState({
    truncated: false,
    nextAfterPage: null,
    isLoadingMore: false,
  });
  const [completedSearchQuery, setCompletedSearchQuery] = useState("");
  const [searchProgress, setSearchProgress] = useState({
    done: 0,
    total: 0,
    isSearching: false,
  });
  const fetchingOcrPages = useRef(new Set());
  const highlightMeasureContext = useRef(null);
  const activeGeometryDocumentId = useRef(null);
  const observer = useRef();
  const pageVisibility = useRef(new Map());
  const [defaultZoom, setdefaultZoom] = useState("fit");
  const [slotsData, setSlotsData] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [documentAction, setDocumentAction] = useState(null);
  const caseId = documentData?.for_case?.id || getCaseId();
  const clientId = documentData?.for_client?.id || getClientId();

  useEffect(() => {
    if (isVisible) setSearchString(initialSearchTerm);
  }, [documentURL, initialSearchTerm, isVisible]);

  const handleHideDocumentModal = () => {
    setSearchString("");
    hideDocumentModal();
  };
  const onZoomChange = (zoomType) => {
    const containerWidth = containerRef?.offsetWidth || 0;
    const containerHeight = containerRef?.offsetHeight || 0;
    switch (zoomType) {
      case "in":
        setZoomLevel((prevZoom) => Math.min(prevZoom + 0.1, 4));
        setdefaultZoom("");
        break;
      case "out":
        setZoomLevel((prevZoom) => Math.max(prevZoom - 0.1, 0.5));
        setdefaultZoom("");
        break;
      case "width": {
        if (!pageDimensions.width) return;
        const widthScale = containerWidth / pageDimensions.width;
        const roundedWidthScale = Math.max(Math.floor(widthScale * 10) / 10 - 0.1, 0.1);
        setZoomLevel(roundedWidthScale);
        setdefaultZoom("");
        break;
      }
      case "height": {
        if (!pageDimensions.height) return;
        const heightScale = containerHeight / pageDimensions.height;
        setZoomLevel(heightScale);
        setdefaultZoom("");
        break;
      }
      case "fit": {
        if (!pageDimensions.width || !pageDimensions.height) return;
        const fitScale = Math.min(
          containerWidth / pageDimensions.width,
          containerHeight / pageDimensions.height
        );
        setZoomLevel(Math.max(fitScale - 0.05, 0.1));
        setdefaultZoom("fit");
        break;
      }
      default:
        if (typeof zoomType === "number") {
          setZoomLevel(zoomType);
          setdefaultZoom("");
        }
        break;
    }
  };

  async function onDocumentLoadSuccess(pdf) {
    setNumPages(pdf?.numPages || 0);
    setError("");
  }

  // Drop everything derived from the previous document so a stale proxy is
  // never searched and stale sizes never size the new document's placeholders.
  useEffect(() => {
    setNumPages(null);
    setError("");
    setSearchError("");
    setPage(1);
    setZoomLevel(1);
    setdefaultZoom("fit");
    setPageSizes({});
    setPageDimensions({ width: 0, height: 0 });
    setOcrGeometryByPage({});
    setOcrSearchPages([]);
    setOcrSearchPagination({ truncated: false, nextAfterPage: null, isLoadingMore: false });
    setCompletedSearchQuery("");
    setSearchProgress({ done: 0, total: 0, isSearching: false });
    fetchingOcrPages.current.clear();
    pageVisibility.current.clear();
    baselineSized.current = false;
  }, [documentURL, isVisible]);

  function scrollPageToView(pageNumber, updatePageNumber = (pageNumber) => pageNumber) {
    const targetPage = displayedPageNumbers.includes(pageNumber)
      ? pageNumber
      : pageNumber > page
        ? displayedPageNumbers.find((number) => number >= pageNumber)
        : [...displayedPageNumbers].reverse().find((number) => number <= pageNumber);
    const pageElementId = `pdf_page_${targetPage}`;
    const pageElement = containerRef?.querySelector(`[id="${pageElementId}"]`);
    if (pageElement) {
      pageElement.scrollIntoView({ behavior: "smooth" });
      updatePageNumber(targetPage);
    }
  }
  useEffect(() => {
    const handleResize = () => {
      if (!containerRef || !pageDimensions.width || !pageDimensions.height) return;

      if (pageDimensions.width && pageDimensions.height) {
        const containerWidth = containerRef.offsetWidth;
        if (defaultZoom === "fit") {
          handleZoomDefault(pageDimensions.width, pageDimensions.height);
        } else {
          const widthScale = containerWidth / pageDimensions.width;
          const roundedScale = Math.max(widthScale - 0.05, 0.1);
          setZoomLevel(roundedScale);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [pageDimensions, containerRef, defaultZoom]);

  const onPageLoadSuccess = (page) => {
    const { width, height } = page.getViewport({ scale: 1 });

    setPageSizes((prev) =>
      prev[page.pageNumber]?.width === width && prev[page.pageNumber]?.height === height
        ? prev
        : { ...prev, [page.pageNumber]: { width, height } }
    );

    // by whichever page happened to load last.
    if (baselineSized.current) return;
    baselineSized.current = true;

    setPageDimensions({
      width: width,
      height: height,
    });
    if (defaultZoom === "fit") {
      handleZoomDefault(width, height);
    }
  };

  const handleZoomDefault = (width, height) => {
    if (containerRef) {
      const containerWidth = containerRef?.offsetWidth || 0;
      const containerHeight = containerRef?.offsetHeight || 0;

      const fitScale = Math.min(containerWidth / width, containerHeight / height);
      setZoomLevel(Math.max(fitScale - 0.05, 0.1));
    }
  };

  const documentId = documentData?.id || documentData?.document_id;
  activeGeometryDocumentId.current = documentId;

  useEffect(() => {
    const query = debouncedSearchString.trim();
    setSearchError("");
    if (!isVisible || !documentId || !numPages || !query) {
      setOcrSearchPages([]);
      setOcrSearchPagination({ truncated: false, nextAfterPage: null, isLoadingMore: false });
      setCompletedSearchQuery("");
      setSearchProgress({ done: 0, total: numPages || 0, isSearching: false });
      return;
    }

    const controller = new AbortController();
    setOcrSearchPages([]);
    setOcrSearchPagination({ truncated: false, nextAfterPage: null, isLoadingMore: false });
    setCompletedSearchQuery("");
    setSearchProgress({ done: 0, total: numPages, isSearching: true });

    const fetchFirstSearchWindow = async () => {
      const response = await api_without_cancellation.get(
        `/api/document/${documentId}/ocr-search/`,
        { params: { q: query }, signal: controller.signal }
      );
      const responseData = response.data?.data || response.data;
      const matchingPages = Array.isArray(responseData?.pages)
        ? responseData.pages
            .map((result) =>
              Number(typeof result === "object" && result !== null ? result.page : result)
            )
            .filter((pageNumber) => pageNumber >= 1 && pageNumber <= numPages)
        : [];

      setOcrSearchPages(matchingPages);
      setOcrSearchPagination({
        truncated: responseData?.truncated === true,
        nextAfterPage: responseData?.next_after_page ?? null,
        isLoadingMore: false,
      });
      setCompletedSearchQuery(query);
      setSearchProgress({
        done: matchingPages.length,
        total: Number(responseData?.total_match_pages) || matchingPages.length,
        isSearching: false,
      });

      if (matchingPages.length) scrollPageToView(matchingPages[0], setPage);
    };

    fetchFirstSearchWindow().catch((error) => {
      if (error.name === "CanceledError" || error.name === "AbortError") return;
      console.error("Failed to search OCR document:", error);
      setSearchError("Unable to search this document. Please try again.");
      setCompletedSearchQuery("");
      setSearchProgress({ done: 0, total: numPages, isSearching: false });
    });

    return () => controller.abort();
  }, [debouncedSearchString, documentId, isVisible, numPages]);

  useEffect(() => {
    const query = debouncedSearchString.trim();
    const lastLoadedMatch = ocrSearchPages[ocrSearchPages.length - 1];
    if (
      !isVisible ||
      !query ||
      completedSearchQuery !== query ||
      !ocrSearchPagination.truncated ||
      ocrSearchPagination.nextAfterPage === null ||
      !lastLoadedMatch ||
      page < lastLoadedMatch
    ) {
      return;
    }

    const controller = new AbortController();
    const cursor = ocrSearchPagination.nextAfterPage;
    setSearchProgress((previous) => ({ ...previous, isSearching: true }));

    api_without_cancellation
      .get(`/api/document/${documentId}/ocr-search/`, {
        params: { q: query, after_page: cursor },
        signal: controller.signal,
      })
      .then((response) => {
        const responseData = response.data?.data || response.data;
        const nextPages = Array.isArray(responseData?.pages)
          ? responseData.pages
              .map((result) =>
                Number(typeof result === "object" && result !== null ? result.page : result)
              )
              .filter((pageNumber) => pageNumber >= 1 && pageNumber <= numPages)
          : [];

        setOcrSearchPages((previous) =>
          Array.from(new Set([...previous, ...nextPages])).sort((a, b) => a - b)
        );
        setOcrSearchPagination({
          truncated: responseData?.truncated === true,
          nextAfterPage: responseData?.next_after_page ?? null,
          isLoadingMore: false,
        });
        setSearchProgress((previous) => ({
          done: previous.done + nextPages.length,
          total: Number(responseData?.total_match_pages) || previous.total,
          isSearching: false,
        }));
      })
      .catch((error) => {
        if (error.name === "CanceledError" || error.name === "AbortError") return;
        console.error("Failed to load the next OCR search window:", error);
        setSearchProgress((previous) => ({ ...previous, isSearching: false }));
      });

    return () => controller.abort();
  }, [
    completedSearchQuery,
    debouncedSearchString,
    documentId,
    numPages,
    isVisible,
    ocrSearchPages,
    ocrSearchPagination,
    page,
  ]);

  const getPageHighlights = (pageNumber = ocrSearchPages[0]) => {
    const query = debouncedSearchString.trim().toLowerCase();
    if (!query) return [];

    const geometry = ocrGeometryByPage[pageNumber] || {};
    const getNormalizedBox = (box) => {
      if (!box) return null;

      const left = Number(box.left);
      const top = Number(box.top);
      const width = Number(box.width);
      const height = Number(box.height);

      if (![left, top, width, height].every(Number.isFinite)) return null;
      if (left < 0 || top < 0 || width <= 0 || height <= 0 || left >= 1 || top >= 1) return null;

      // The API's coordinate_space is normalized_top_left. Clamp boxes at the
      // page edge so malformed OCR coordinates cannot overflow the PDF canvas.
      return {
        left,
        top,
        width: Math.min(width, 1 - left),
        height: Math.min(height, 1 - top),
      };
    };

    const lines = Array.isArray(geometry.lines) ? geometry.lines : [];
    return lines.flatMap((line, lineIndex) => {
      const lineText = String(line.text || "");
      const normalizedLineText = lineText.toLowerCase();
      const box = getNormalizedBox(line.bounding_box);
      if (!box || !lineText.length) return [];

      const matches = [];
      let startIndex = normalizedLineText.indexOf(query);

      // The API provides a box for the complete line, so locate the searched
      // substring proportionally inside it instead of highlighting the line.
      // Canvas measurement accounts for proportional characters (for example,
      // "i" is much narrower than "m"); raw character counts clipped the last
      // letters of matches such as "thera".
      if (!highlightMeasureContext.current && typeof document !== "undefined") {
        highlightMeasureContext.current = document.createElement("canvas").getContext("2d");
        if (highlightMeasureContext.current) {
          highlightMeasureContext.current.font = "100px Arial, sans-serif";
        }
      }
      const measureContext = highlightMeasureContext.current;
      const fullTextWidth = measureContext?.measureText(lineText).width || lineText.length;

      while (startIndex !== -1) {
        const prefixWidth =
          measureContext?.measureText(lineText.slice(0, startIndex)).width || startIndex;
        const matchWidth =
          measureContext?.measureText(lineText.slice(startIndex, startIndex + query.length))
            .width || query.length;
        const left = box.left + box.width * (prefixWidth / fullTextWidth);

        matches.push({
          left,
          top: box.top,
          width: Math.min(box.width * (matchWidth / fullTextWidth), box.left + box.width - left),
          height: box.height,
          key: `${pageNumber}-${line.index ?? lineIndex}-${startIndex}`,
        });
        startIndex = normalizedLineText.indexOf(query, startIndex + query.length);
      }

      return matches;
    });
  };

  const normalizedSearchString = searchString.trim();
  const isShowingSearchResults =
    Boolean(normalizedSearchString) &&
    normalizedSearchString === debouncedSearchString.trim() &&
    completedSearchQuery === debouncedSearchString.trim() &&
    !searchProgress.isSearching;
  const allPageNumbers = Array.from({ length: numPages || 0 }, (_, index) => index + 1);
  const displayedPageNumbers = isShowingSearchResults ? ocrSearchPages : allPageNumbers;

  // Pages close enough to the viewport to be worth rendering. Everything else
  // stays a sized placeholder, which is what stops pdf.js fetching the bytes
  // for the whole document.
  const visiblePages = usePdfPageWindow(containerRef, displayedPageNumbers.length, {
    observationKey: displayedPageNumbers.join(","),
    initialKeys: [displayedPageNumbers[0] || 1],
  });

  // Sizes placeholders for pages that have not rendered yet. Correct for the
  // uniform-page documents this app handles, and each page corrects itself the
  // first time it renders.
  const baselineSize = pageDimensions.width ? pageDimensions : null;

  useEffect(() => {
    if (!containerRef) return;

    const options = {
      root: containerRef,
      rootMargin: "0px",
      threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
    };

    const callback = (entries) => {
      entries.forEach((entry) => {
        const pageNumber = Number(entry.target.getAttribute("data-page-number"));
        pageVisibility.current.set(pageNumber, entry.isIntersecting ? entry.intersectionRatio : 0);
      });

      const mostVisiblePage = Array.from(pageVisibility.current.entries()).reduce(
        (best, current) => (current[1] > best[1] ? current : best),
        [null, 0]
      );
      if (mostVisiblePage[0] !== null) setPage(mostVisiblePage[0]);
    };

    observer.current = new IntersectionObserver(callback, options);

    // Scoped to this viewer's container: `.pdf-page` is also used by
    // AssembleDocumentModal and LibraryDocumentViewer, and a document-wide
    // query picked up their pages too.
    const pages = containerRef.querySelectorAll(".pdf-page");
    pages.forEach((page) => {
      observer.current.observe(page);
    });

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
      pageVisibility.current.clear();
    };
  }, [containerRef, numPages, displayedPageNumbers.join(",")]);
  useEffect(() => {
    const query = debouncedSearchString.trim();
    if (
      !isVisible ||
      !documentId ||
      !numPages ||
      !query ||
      completedSearchQuery !== query ||
      !ocrSearchPages.length
    ) {
      return;
    }

    const fetchGeo = async () => {
      const pagesToFetch = ocrSearchPages.filter(
        (matchPage) =>
          Math.abs(matchPage - page) <= 2 &&
          !ocrGeometryByPage[matchPage] &&
          !fetchingOcrPages.current.has(matchPage)
      );
      if (!pagesToFetch.length) return;
      pagesToFetch.forEach((pageNumber) => fetchingOcrPages.current.add(pageNumber));

      try {
        const response = await api_without_cancellation.get(
          `/api/document/${documentId}/ocr-geometry/`,
          { params: { pages: pagesToFetch.join(",") } }
        );

        if (activeGeometryDocumentId.current !== documentId) return;

        const responseData = response.data?.data || response.data;
        const geometries = Array.isArray(responseData)
          ? responseData
          : responseData?.pages || responseData?.results || responseData?.geometries || [];

        setOcrGeometryByPage((previous) => {
          const next = { ...previous };
          pagesToFetch.forEach((pageNumber) => {
            next[pageNumber] = { lines: [] };
          });
          geometries.forEach((geometry) => {
            const pageNumber = Number(geometry.page ?? geometry.page_number ?? geometry.pageNumber);
            if (Number.isFinite(pageNumber)) next[pageNumber] = geometry;
          });
          return next;
        });
      } catch (error) {
        console.error("Failed to fetch OCR geometry:", error);
      } finally {
        pagesToFetch.forEach((pageNumber) => fetchingOcrPages.current.delete(pageNumber));
      }
    };

    fetchGeo();
  }, [
    completedSearchQuery,
    debouncedSearchString,
    documentId,
    isVisible,
    numPages,
    ocrGeometryByPage,
    ocrSearchPages,
    page,
  ]);
  const fileExtension =
    (documentData?.upload || documentURL || "").split(/[?#]/)[0].match(/\.[a-z0-9]+$/i)?.[0] ||
    ".pdf";
  const documentName = documentData?.file_name || "Untitled Document";
  const viewerFileName = documentName.toLowerCase().endsWith(fileExtension.toLowerCase())
    ? documentName
    : `${documentName}${fileExtension}`;

  useEffect(() => {
    setSlotsData([]);
    setLocationError(false);
    if (!isVisible || !caseId || !clientId) return;

    const controller = new AbortController();
    setLocationLoading(true);
    api_without_cancellation
      .get("/api/doc-page/doc-page-rows/", {
        params: { page_id: "", case_id: caseId, client_id: clientId },
        signal: controller.signal,
      })
      .then((response) => {
        if (!controller.signal.aborted) setSlotsData(response.data.data || []);
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          console.error("Failed to load original document location:", error);
          setLocationError(true);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLocationLoading(false);
      });
    return () => controller.abort();
  }, [caseId, clientId, documentId, isVisible]);

  let originalPage = documentData.page || documentData.document_slot?.page;
  let originalSlot = documentData.document_slot;
  let originalPanel = documentData.panel;
  for (const row of slotsData) {
    const slot = row.slots?.find((slot) =>
      slot.docs?.some((doc) => String(doc.id) === String(documentId))
    );
    const panel = [...(row.panels || []), ...(row.inactive_panels || [])].find((panel) =>
      panel.docs?.some((doc) => String(doc.id) === String(documentId))
    );
    if (slot || panel) {
      const originalDoc = (slot || panel).docs.find((doc) => String(doc.id) === String(documentId));
      originalPage = row.page;
      originalSlot = originalDoc.document_slot || slot;
      originalPanel = panel;
      break;
    }
  }
  const clientName = [documentData.for_client?.last_name, documentData.for_client?.first_name]
    .filter(Boolean)
    .join(", ");

  const handleDocumentAction = async (action) => {
    setDocumentAction(action);
    try {
      const response = await api_without_cancellation.post("/api/delete-doc-api/", {
        doc_id: documentId,
        check: "",
      });
      const bytes = Uint8Array.from(atob(response.data.data), (char) => char.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      if (action === "download") {
        const link = document.createElement("a");
        link.href = url;
        link.download = response.data.filename || viewerFileName;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else {
        const iframe = document.createElement("iframe");
        iframe.hidden = true;
        iframe.onload = () => {
          const cleanup = () => {
            iframe.remove();
            URL.revokeObjectURL(url);
          };
          iframe.contentWindow.addEventListener("afterprint", cleanup, { once: true });
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        };
        iframe.src = url;
        document.body.appendChild(iframe);
      }
    } catch (error) {
      console.error(`Failed to ${action} linked document:`, error);
      setError(`Failed to ${action} this document. Please try again.`);
    } finally {
      setDocumentAction(null);
    }
  };

  if (!isVisible) return null;

  return (
    <Modal
      show={isVisible}
      onHide={handleHideDocumentModal}
      size="xl"
      aria-labelledby={titleId}
      centered
      className="universal-document-modal custom-modal-width document-link-viewer"
      dialogClassName="document-modal-padding-setting"
    >
      <Modal.Header className="document-modal-header height-25 border-primary">
        <Modal.Title id={titleId} className="text-truncate" title={viewerFileName}>
          Document Link Viewer: {viewerFileName}
        </Modal.Title>
        <button
          type="button"
          className="document-link-close"
          aria-label="Close"
          onClick={handleHideDocumentModal}
        >
          <span aria-hidden="true">×</span>
        </button>
      </Modal.Header>
      <Modal.Body className="document-modal-body">
        <div className="document-viewer-shell d-flex flex-column m-0">
          <div className="document-viewer-content d-flex">
            <PdfThumbnailRail scrollRef={setRailScrollEl} portalRef={setRailPortalEl} />
            <div className="document-viewer-center d-flex flex-column">
              <div className="search-and-error-area">
                {(error || searchError) && (
                  <div className="alert alert-danger m-1" role="alert">
                    {error || searchError}
                  </div>
                )}
                <div className="document-viewer-control-row m-1">
                  <label className="document-viewer-control-label" htmlFor={documentSearchId}>
                    Search in Document:
                  </label>
                  <input
                    id={documentSearchId}
                    type="text"
                    autoComplete="off"
                    className="form-control document-viewer-term-search document-search-text-color py-0 height-25"
                    placeholder="Type Search Terms to Only Display Pages With Those Terms"
                    value={searchString}
                    onChange={(event) => setSearchString(event.target.value)}
                  />
                </div>
                {searchProgress.isSearching && (
                  <div className="document-viewer-search-progress" role="status">
                    Searching...
                  </div>
                )}
              </div>
              <div
                className="main-doc-area m-0"
                ref={setContainerRef}
                style={{
                  width: "100%",
                  backgroundColor: "var(--primary-20)",
                  overflowY: "scroll",
                  overflowX: "auto",
                  scrollbarWidth: "none",
                }}
              >
                <Document
                  file={documentURL}
                  options={PDF_LOAD_OPTIONS}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={() => setError("Failed to load this file")}
                >
                  {railPortalEl &&
                    createPortal(
                      <PdfThumbnailPages
                        numPages={numPages}
                        pageNumbers={displayedPageNumbers}
                        currentPage={page}
                        scrollRootEl={railScrollEl}
                        onThumbnailClick={scrollPageToView}
                        setPageNumber={setPage}
                      />,
                      railPortalEl
                    )}
                  {isShowingSearchResults && !displayedPageNumbers.length && (
                    <div className="text-center p-3" role="status">
                      No matching pages found.
                    </div>
                  )}
                  {displayedPageNumbers.map((pageNumber) => {
                    const size = pageSizes[pageNumber] || baselineSize;
                    return (
                      <div
                        key={pageNumber}
                        id={`pdf_page_${pageNumber}`}
                        data-page-number={pageNumber}
                        data-window-key={pageNumber}
                        className="pdf-page"
                        style={{
                          ...pdfPageStyle,
                          width: size ? size.width * zoomLevel : "min-content",
                          height: size ? size.height * zoomLevel : undefined,
                        }}
                      >
                        {visiblePages.has(pageNumber) && (
                          <>
                            <Page
                              scale={zoomLevel}
                              pageNumber={pageNumber}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                              onLoadSuccess={onPageLoadSuccess}
                              className="pdf-page-content"
                            />
                            <div className="document-link-highlights" aria-hidden="true">
                              {getPageHighlights(pageNumber).map((highlight) => (
                                <span
                                  key={highlight.key}
                                  style={{
                                    left: `calc(${highlight.left * 100}% - 2.5px)`,
                                    top: `calc(${highlight.top * 100}% - 2.5px)`,
                                    width: `calc(${highlight.width * 100}% + 5px)`,
                                    height: `calc(${highlight.height * 100}% + 5px)`,
                                  }}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </Document>
              </div>
            </div>
            <aside className="document-link-sidebar" aria-label="Linked document details">
              <div className="document-link-sidebar-details bg-white">
                <section aria-label="Case">
                  <div
                    role="heading"
                    aria-level={2}
                    className="document-link-section-title document-pop-up-menu-title"
                  >
                    CASE
                  </div>
                  {clientName && (
                    <div className="document-link-case-row document-link-client-name">
                      <img
                        src={documentData.for_client?.profile_pic_29p || avatarIcon}
                        alt=""
                        className="document-link-case-icon"
                      />
                      {clientName}
                    </div>
                  )}
                  {documentData.for_client?.birthday && (
                    <div className="document-link-case-row document-link-birthday-row">
                      <img src={birthdayIcon} alt="" className="document-link-case-icon" />
                      <span>{formatCalendarDate(documentData.for_client.birthday)}</span>
                      <span className="document-link-age-label">age</span>
                      <span>{calculateAge(documentData.for_client.birthday)}</span>
                    </div>
                  )}
                  {documentData.for_case?.case_type?.name && (
                    <div className="document-link-case-row">
                      {documentData.for_case.case_type.casetype_icon && (
                        <img
                          src={documentData.for_case.case_type.casetype_icon}
                          alt=""
                          className="document-link-case-icon"
                        />
                      )}
                      {documentData.for_case.case_type.name}
                    </div>
                  )}
                  {documentData.for_case?.incident_date && (
                    <div className="document-link-case-row">
                      <img src={incidentIcon} alt="" className="document-link-case-icon" />
                      {formatCalendarDate(documentData.for_case.incident_date)}
                    </div>
                  )}
                  <div className="document-link-uploaded-row">
                    <span>Uploaded:</span>
                    <span>
                      {formatCalendarDate(
                        documentData.created ||
                          documentData.uploaded_at ||
                          documentData.date_uploaded
                      ) || "—"}
                    </span>
                  </div>
                </section>
                <section aria-label="Original Doc Location">
                  <div
                    role="heading"
                    aria-level={2}
                    className="document-link-section-title document-pop-up-menu-title"
                  >
                    Original Doc Location
                  </div>
                  <div className="document-link-location">
                    {originalPage?.name ? (
                      <>
                        <div className="document-link-location-row">
                          {originalPage.page_icon && (
                            <img
                              src={originalPage.page_icon}
                              alt=""
                              className="document-link-case-icon"
                            />
                          )}
                          <span>
                            {originalPage.name} Page
                            {/* {!originalSlot?.slot_number ? " Generally" : ""} */}
                          </span>
                        </div>
                        {originalPanel?.panel_name && (
                          <div
                            className="document-link-location-row"
                            style={{ paddingLeft: "20px" }}
                            title={originalPanel.panel_name}
                          >
                            <span className="text-truncate">{originalPanel.panel_name}</span>
                          </div>
                        )}
                        {Boolean(originalSlot?.slot_number) && (
                          <div
                            className="document-link-location-row"
                            title={originalSlot.slot_name || documentName}
                          >
                            <span className="text-primary-25 text-center" style={{ width: "15px" }}>
                              {originalSlot.slot_number}.
                            </span>
                            <span className="text-truncate">
                              {originalSlot.slot_name || documentName}
                            </span>
                          </div>
                        )}
                      </>
                    ) : locationLoading ? (
                      "Loading..."
                    ) : locationError ? (
                      "Unable to load original location"
                    ) : (
                      "Case Page"
                    )}
                  </div>
                </section>
                <section aria-label="Doc Link To">
                  <div
                    role="heading"
                    aria-level={2}
                    className="document-link-section-title document-pop-up-menu-title"
                  >
                    Doc Link To
                  </div>
                  <div className="document-link-location document-link-target-location">
                    {docLinkTo || "No link target provided"}
                  </div>
                </section>
              </div>
              <div className="document-link-sidebar-actions">
                <div className="document-link-file-actions">
                  <Button
                    variant="primary"
                    disabled={!documentId || Boolean(documentAction)}
                    onClick={() => handleDocumentAction("print")}
                  >
                    <img src={printIcon} alt="" className="document-link-case-icon" />
                    Print
                  </Button>
                  <Button
                    variant="primary"
                    disabled={!documentId || Boolean(documentAction)}
                    onClick={() => handleDocumentAction("download")}
                  >
                    <img src={downloadIcon} alt="" className="document-link-case-icon" />
                    Download
                  </Button>
                </div>
                <Button
                  variant="success"
                  disabled={!onNewDocLink}
                  onClick={() => {
                    handleHideDocumentModal();
                    onNewDocLink();
                  }}
                >
                  New Doc Link
                </Button>
              </div>
            </aside>
          </div>
          <PdfNavigation
            alignWithDocument
            documentViewport={containerRef}
            showSearch={false}
            totalPages={numPages}
            pageNumber={page}
            setPageNumber={setPage}
            onZoomChange={onZoomChange}
            zoomScale={zoomLevel}
            scrollPageToView={scrollPageToView}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DocumentLinkViewer;
